/**
 * Draws a view with Softmax's components. Values are written into the view: it reads nothing and writes nothing, and the
 * only way out is a click on one of its actions.
 */
import { Component, useMemo, type ReactNode } from 'react'
import { Avatar, Badge, Button, Empty, Section, Tabs } from '../base/ui.tsx'
import { Markdown } from '../base/Markdown.tsx'
import { ChartPanel } from '../chart/Chart.tsx'
import type { ChartData } from '../chart/chart-math.ts'
import { DataTable, Metric, RowList, type Column, type RowItem } from '../components/data.tsx'
import { Heading, NamedAvatar, Text } from '../components/content.tsx'
import { Frame, Grid, PanelTabs, Stack, type Gap } from '../components/layout.tsx'
import { Icon } from '../icons.tsx'
import { component, type ComponentDef } from '../spec/catalog.ts'
import type { Format } from '../spec/format.ts'
import type { IconName } from '../spec/icons.ts'
import { resolveProps } from '../spec/resolve.ts'
import { readPath, type Action, type Issue } from '../spec/schema.ts'
import type { View as CogentView, ViewNode } from '../spec/types.ts'
import { checkResolved } from '../spec/validate.ts'
import { CustomFrame } from './CustomFrame.tsx'

export type { CogentView, ViewNode }

/**
 * What a click does; the host decides how `ask` is said. `said` is what the user has said since the view appeared: an
 * option they said is the one they chose, so the choice comes back with the history and needs no storage of its own.
 */
export type ViewHost = { ask: (text: string) => void; busy?: boolean; said?: string[] }

type Scope = Record<string, unknown>

export function saidAfter<M extends { from: string; text: string }>(messages: M[], message: M) {
  return messages
    .slice(messages.indexOf(message) + 1)
    .filter(({ from }) => from === 'you')
    .map(({ text }) => text)
}

const isChosenAction = (action: unknown, host: ViewHost) => {
  const ask = (action as { ask?: unknown } | null | undefined)?.ask
  return typeof ask === 'string' && host.said?.includes(ask) === true
}

function follow(action: Action, host: ViewHost) {
  if ('ask' in action) return host.ask(action.ask)

  if (action.open.startsWith('https://')) window.open(action.open, '_blank', 'noopener,noreferrer')
  else window.location.assign(action.open)
}

function Problems({ issues }: { issues: Issue[] }) {
  return (
    <div className="ui-view-issue" role="alert">
      {issues.map((issue, index) => (
        <p key={index}>
          <code>{issue.path || 'view'}</code> {issue.message}
        </p>
      ))}
    </div>
  )
}

function ActionButton({
  label,
  action,
  host,
  variant,
  icon,
}: {
  label: string
  action: Action
  host: ViewHost
  variant?: 'primary' | 'secondary' | 'ghost'
  icon?: IconName | undefined
}) {
  const chosen = isChosenAction(action, host)
  const asking = 'ask' in action
  return (
    <Button
      {...(variant ? { variant } : {})}
      {...(icon ? { icon } : {})}
      data-chosen={chosen ? '' : undefined}
      aria-pressed={chosen ? true : undefined}
      disabled={chosen || (asking && host.busy === true)}
      onClick={() => follow(action, host)}
    >
      {label}
    </Button>
  )
}

/** Buttons side by side are one set of options: once one is chosen, the rest go. */
function Children({ nodes, scope, host }: { nodes: unknown; scope: Scope; host: ViewHost }) {
  if (!Array.isArray(nodes)) return null

  const list = nodes as ViewNode[]
  const chosen = list.find((node) => node.type === 'button' && isChosenAction(node.action, host))
  return list.map((node, index) =>
    chosen && node.type === 'button' && node !== chosen ? null : (
      <Node key={index} node={node} scope={scope} host={host} />
    )
  )
}

const INLINE = new Set(['badge', 'button', 'avatar'])
const MAX_REPEATED = 100
const REPEATED = '__repeated'

function Node({ node, scope, host }: { node: ViewNode; scope: Scope; host: ViewHost }) {
  const def = component(node.type)

  // Checked views can't get here, but a list read through a binding isn't checked until now.
  if (!def) return <Problems issues={[{ path: 'type', message: `there's no "${String(node.type)}" component` }]} />

  return <KnownNode def={def} node={node} scope={scope} host={host} />
}

function KnownNode({ def, node, scope, host }: { def: ComponentDef; node: ViewNode; scope: Scope; host: ViewHost }) {
  const { props, issues } = useMemo(() => resolveProps(def, node, scope), [def, node, scope])
  const problems = useMemo(() => [...issues, ...checkResolved(def, props)], [def, issues, props])

  if (problems.length) return <Problems issues={problems} />

  // Checked views can't get here either, but copies multiply, so this holds through a binding too.
  if (REPEATED in scope && (def.type === 'repeat' || def.type === 'custom'))
    return <Problems issues={[{ path: 'template', message: `can't hold a ${def.type}` }]} />

  const drawn = drawNode(node, props, scope, host)
  // Small things keep their own width in a column (a column stretches its children).
  return INLINE.has(def.type) ? <div className="ui-view-inline">{drawn}</div> : drawn
}

type SeriesProp = {
  name: string
  points?: { x: number | string; y: number }[]
  rows?: Record<string, unknown>[]
  x_field?: string
  y_field?: string
}

/** A series' points: as written, or read from its rows by `x_field` and `y_field` (rows without a number for y are left out). */
function seriesPoints({ rows, x_field, y_field, points, ...rest }: SeriesProp) {
  if (points || !rows || !x_field || !y_field) return { ...rest, points: points ?? [] }

  const num = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN)
  const read = rows.map((r) => ({ x: readPath(r, x_field) as number | string, y: num(readPath(r, y_field)) }))
  return { ...rest, points: read.filter((pt) => pt.x !== undefined && pt.x !== null && Number.isFinite(pt.y)) }
}

function drawNode(node: ViewNode, p: Scope, scope: Scope, host: ViewHost): ReactNode {
  const s = (key: string) => p[key] as string
  const kids = (nodes: unknown) => <Children nodes={nodes} scope={scope} host={host} />
  switch (node.type) {
    case 'stack':
      return (
        <Stack
          direction={p.direction as 'down'}
          gap={p.gap as Gap}
          align={p.align as 'stretch'}
          justify={p.justify as 'start'}
          wrap={p.wrap as boolean}
        >
          {kids(node.children)}
        </Stack>
      )
    case 'grid':
      return (
        <Grid columns={p.columns as number} min={p.min as number} gap={p.gap as Gap}>
          {kids(node.children)}
        </Grid>
      )
    case 'frame':
      return (
        <Frame title={s('title')} description={s('description')} aside={node.aside ? kids(node.aside) : null}>
          {kids(node.children)}
        </Frame>
      )
    case 'section':
      return (
        <Section label={s('label')} aside={node.aside ? kids(node.aside) : null}>
          <div className="ui-view-section-body">{kids(node.children)}</div>
        </Section>
      )
    case 'tabs':
      return (
        <PanelTabs
          tabs={(p.tabs as { label: string; count?: number; body: ViewNode[] }[]).map((tab) => ({
            label: tab.label,
            ...(tab.count === undefined ? {} : { count: tab.count }),
            body: <div className="ui-view-section-body">{kids(tab.body)}</div>,
          }))}
        />
      )
    case 'repeat': {
      const limit = Math.min((p.limit as number | undefined) ?? MAX_REPEATED, MAX_REPEATED)
      const items = (p.items as unknown[]).slice(0, limit)
      return items.map((item, index) => (
        <Node
          key={index}
          node={node.template as ViewNode}
          scope={{ ...scope, [s('as')]: item, [REPEATED]: true }}
          host={host}
        />
      ))
    }
    case 'heading':
      return <Heading text={s('text')} meta={s('meta')} level={p.level as 1 | 2} />
    case 'text':
      return (
        <Text tone={p.tone as 'default'} size={p.size as 'body'} mono={p.mono as boolean}>
          {s('text')}
        </Text>
      )
    case 'markdown':
      return <Markdown>{s('text')}</Markdown>
    case 'badge':
      return (
        <Badge tone={p.tone as 'neutral'}>
          {p.icon ? <Icon name={p.icon as IconName} size={12} /> : null}
          {s('text')}
        </Badge>
      )
    case 'avatar':
      return p.with_name ? (
        <NamedAvatar name={s('name')} src={s('src')} size={p.size as number} />
      ) : (
        <Avatar name={s('name')} src={s('src')} size={p.size as number} />
      )
    case 'metric':
      return (
        <Metric
          label={s('label')}
          value={p.value as number}
          format={p.format as Format}
          unit={s('unit')}
          delta={p.delta as number}
          better={p.better as 'higher'}
          caption={s('caption')}
          trend={p.trend as number[]}
        />
      )
    case 'chart':
      return (
        <ChartPanel
          data={
            {
              title: s('title'),
              chart: p.chart,
              x: p.x,
              y: p.y,
              series: (p.series as SeriesProp[]).map(seriesPoints),
            } as ChartData
          }
          description={s('description')}
        />
      )
    case 'table':
      return (
        <DataTable
          columns={p.columns as Column[]}
          rows={p.rows as Record<string, unknown>[]}
          limit={p.limit as number}
          empty={s('empty')}
        />
      )
    case 'list':
      return <RowList items={p.items as RowItem[]} empty={s('empty')} />
    case 'nav': {
      const items = p.items as { label: string; href: string; count?: number; current?: boolean }[]
      const current = items.findIndex((item) => item.current)
      return (
        <Tabs
          label={s('label')}
          current={String(current)}
          tabs={items.map((item, index) => ({
            id: String(index),
            label: item.label,
            href: item.href,
            count: item.count ?? null,
          }))}
        />
      )
    }
    case 'button':
      return (
        <ActionButton
          label={s('label')}
          action={p.action as Action}
          host={host}
          variant={p.variant as 'secondary'}
          icon={p.icon as IconName}
        />
      )
    case 'empty': {
      const next = p.action as { label: string; action: Action } | undefined
      return (
        <Empty action={next ? <ActionButton label={next.label} action={next.action} host={host} /> : undefined}>
          {s('text')}
        </Empty>
      )
    }
    case 'custom':
      return <CustomFrame html={s('html')} props={(p.props ?? {}) as Scope} height={p.height as number} />
    default:
      return null
  }
}

const NO_SCOPE: Scope = {}

/** The agent writes views, and one that breaks must not take the host's page with it. */
class ViewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override render() {
    return this.state.failed ? <p className="ui-view-issue">This couldn't be shown.</p> : this.props.children
  }
}

export function CogentViewCard({ view, host }: { view: CogentView; host: ViewHost }) {
  return (
    <div className="sc-view ui-view" data-cogent-view="">
      {view.title ? <div className="sc-view-title">{view.title}</div> : null}
      <ViewBoundary>
        <Children nodes={view.body} scope={NO_SCOPE} host={host} />
      </ViewBoundary>
    </div>
  )
}
